import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../lib/api';
import type { ContactRow, CustomerRow, MachineRow, WorkOrderRow } from '../lib/types';
import { CustomerForm, MachineForm } from '../components/forms';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, PageHeader } from '../components/ui';
import { WorkOrderTable } from './WorkOrdersPage';

type Detail = CustomerRow & { contacts: ContactRow[]; machines: MachineRow[]; workOrders: WorkOrderRow[] };

export function CustomerPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const c = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<Detail>(`/api/customers/${id}`) });
  const [editing, setEditing] = useState(false);
  const [contact, setContact] = useState<ContactRow | 'new' | null>(null);
  const [addingMachine, setAddingMachine] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['customer', id] });

  if (c.isLoading || !c.data) return <Loading />;
  const d = c.data;

  const toggleActive = async () => {
    await api.patch(`/api/customers/${d.id}`, { active: !d.active });
    refresh();
  };

  return (
    <>
      <PageHeader title={<>{d.name} {!d.active && <span className="text-base font-normal text-slate-500">(inactive)</span>}</>}>
        <Link to="/work-orders/new"><Button variant="secondary">New Work Order</Button></Link>
        <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
        <Button variant="ghost" onClick={toggleActive}>{d.active ? 'Deactivate' : 'Reactivate'}</Button>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Details">
          <dl className="space-y-1 text-sm">
            <div><dt className="inline text-slate-500">Phone: </dt><dd className="inline">{d.phone ?? '—'}</dd></div>
            <div><dt className="inline text-slate-500">Email: </dt><dd className="inline">{d.email ?? '—'}</dd></div>
            <div><dt className="inline text-slate-500">Billing: </dt><dd className="inline">{d.billingAddress ?? '—'}</dd></div>
            {d.notes && <div className="whitespace-pre-wrap pt-2">{d.notes}</div>}
          </dl>
        </Card>
        <Card title="Contacts" actions={<Button variant="secondary" onClick={() => setContact('new')}>Add</Button>}>
          <ul className="space-y-2 text-sm">
            {d.contacts.map((k) => (
              <li key={k.id} className={k.active ? '' : 'text-slate-400 line-through'}>
                <button className="text-left hover:underline" onClick={() => setContact(k)}>
                  <span className="font-medium">{k.name}</span> {k.phone && `· ${k.phone}`} {k.email && `· ${k.email}`}
                  {k.receivesEmail && <span className="ml-1 text-xs text-green-700">(gets emails)</span>}
                </button>
              </li>
            ))}
            {d.contacts.length === 0 && <li className="text-slate-500">None yet.</li>}
          </ul>
        </Card>
        <Card title="Machines" actions={<Button variant="secondary" onClick={() => setAddingMachine(true)}>Add</Button>}>
          <ul className="space-y-1 text-sm">
            {d.machines.map((m) => (
              <li key={m.id}><Link className="text-blue-800 hover:underline" to={`/machines/${m.id}`}>{m.make} {m.model}</Link> · S/N {m.serialNumber}</li>
            ))}
            {d.machines.length === 0 && <li className="text-slate-500">None yet.</li>}
          </ul>
        </Card>
        <Card title="Work orders" className="lg:col-span-3"><WorkOrderTable rows={d.workOrders.map((w) => ({ ...w, customer: { id: d.id, name: d.name } }))} /></Card>
      </div>

      {editing && (
        <Modal title="Edit customer" onClose={() => setEditing(false)}>
          <CustomerForm initial={d} onSaved={() => { setEditing(false); refresh(); }} onCancel={() => setEditing(false)} />
        </Modal>
      )}
      {addingMachine && (
        <Modal title="Add machine" onClose={() => setAddingMachine(false)}>
          <MachineForm customerId={d.id} onSaved={() => { setAddingMachine(false); refresh(); }} onCancel={() => setAddingMachine(false)} />
        </Modal>
      )}
      {contact && <ContactDialog customerId={d.id} contact={contact === 'new' ? null : contact} onClose={() => setContact(null)} onSaved={() => { setContact(null); refresh(); }} />}
    </>
  );
}

function ContactDialog({ customerId, contact, onClose, onSaved }: { customerId: number; contact: ContactRow | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: contact?.name ?? '', phone: contact?.phone ?? '', email: contact?.email ?? '', receivesEmail: contact?.receivesEmail ?? false, active: contact?.active ?? true });
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      if (contact) await api.patch(`/api/contacts/${contact.id}`, f);
      else await api.post(`/api/customers/${customerId}/contacts`, f);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title={contact ? 'Edit contact' : 'Add contact'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name.trim()}>Save</Button></>}>
      <ErrorText error={error} />
      <Field label="Name" required><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
      <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
      <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.receivesEmail} onChange={(e) => setF({ ...f, receivesEmail: e.target.checked })} /> Receives work order emails</label>
      {contact && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>}
    </Modal>
  );
}

